import { createContext, useMemo, createElement, useContext, forwardRef, useRef, useReducer, useCallback, useLayoutEffect, useEffect } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

var ComponentReactContext = createContext(null);

var FleurContext = function FleurContext(_ref) {
  var value = _ref.value,
      children = _ref.children,
      _ref$options = _ref.options;
  _ref$options = _ref$options === void 0 ? {} : _ref$options;
  var _ref$options$batchedU = _ref$options.batchedUpdate,
      batchedUpdate = _ref$options$batchedU === void 0 ? unstable_batchedUpdates : _ref$options$batchedU,
      _ref$options$synchron = _ref$options.synchronousUpdate,
      synchronousUpdate = _ref$options$synchron === void 0 ? false : _ref$options$synchron;
  useMemo(function () {
    value.storeContext.injectBatch(batchedUpdate);
  }, []);
  return createElement(ComponentReactContext.Provider, {
    value: {
      synchronousUpdate: synchronousUpdate,
      context: value.componentContext
    }
  }, children);
};

var createElementWithContext = function createElementWithContext(context, Component, props) {
  return createElement(FleurContext, {
    value: context,
    children: createElement(Component, props || {})
  });
};

var useFleurContext = function useFleurContext() {
  var _useContext = useContext(ComponentReactContext),
      context = _useContext.context;

  return context;
};
var useInternalFleurContext = function useInternalFleurContext() {
  return useContext(ComponentReactContext);
};

var withFleurContext = function withFleurContext(Component) {
  return forwardRef(function (props, ref) {
    var _useFleurContext = useFleurContext(),
        getStore = _useFleurContext.getStore,
        executeOperation = _useFleurContext.executeOperation,
        depend = _useFleurContext.depend;

    return createElement(Component, Object.assign(Object.assign({
      ref: ref
    }, props), {
      getStore: getStore,
      depend: depend,
      executeOperation: executeOperation
    }));
  });
};

function _typeof(obj) {
  "@babel/helpers - typeof";

  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function (obj) {
      return typeof obj;
    };
  } else {
    _typeof = function (obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(n);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var canUseDOM = typeof window !== 'undefined';
var useIsomorphicLayoutEffect = canUseDOM ? useLayoutEffect : useEffect;

var bounce = function bounce(fn, bounceTime) {
  var lastExecuteTime = 0;

  var bouncer = function bouncer() {
    var now = Date.now();

    if (now - lastExecuteTime > bounceTime) {
      fn();
      lastExecuteTime = now;
    } else {
      setTimeout(bouncer, bounceTime);
    }
  };

  return bouncer;
};

var hasOwnKey = Object.prototype.hasOwnProperty; // Object.is polyfill

var is = function is(x, y) {
  if (x === y) {
    return x !== 0 || y !== 0 || 1 / x === 1 / y;
  } else {
    return x !== x && y !== y;
  }
};
/** Shallow equality check */


var isEqual = function isEqual(prev, next) {
  if (is(prev, next)) return true;
  if (_typeof(prev) !== _typeof(next)) return false;

  if (Array.isArray(prev) && Array.isArray(next)) {
    if (prev.length !== next.length) return false;

    for (var idx in prev) {
      if (!is(prev[idx], next[idx])) return false;
    }
  }

  if (_typeof(prev) === 'object' && _typeof(next) === 'object' && prev !== null && next !== null) {
    for (var key in prev) {
      if (!hasOwnKey.call(next, key)) continue;
      if (!is(prev[key], next[key])) return false;
    }
  }

  return false;
};

var useStore = function useStore(mapStoresToProps) {
  var checkEquality = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : isEqual;

  var _useInternalFleurCont = useInternalFleurContext(),
      getStore = _useInternalFleurCont.context.getStore,
      synchronousUpdate = _useInternalFleurCont.synchronousUpdate;

  var referencedStores = useRef(new Set());
  var isMounted = useRef(false);

  var _useReducer = useReducer(function (s) {
    return s + 1;
  }, 0),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      rerender = _useReducer2[1];

  var getStoreInspector = useCallback(function (storeClass) {
    if (!referencedStores.current.has(storeClass)) {
      referencedStores.current.add(storeClass);

      if (isMounted.current) {
        getStore(storeClass).on(bouncedHandleStoreMutation);
      }
    }

    return getStore(storeClass);
  }, [getStore]);
  var latestState = useRef(null);
  var latestSelector = useRef(mapStoresToProps);

  if (!latestState.current || latestSelector.current !== mapStoresToProps) {
    latestState.current = mapStoresToProps(getStoreInspector);
    latestSelector.current = mapStoresToProps;
  }

  var handleStoreMutation = useCallback(function () {
    var nextState = mapStoresToProps(getStoreInspector);
    if (checkEquality === null || checkEquality === void 0 ? void 0 : checkEquality(latestState.current, nextState)) return;
    latestState.current = nextState;
    rerender();
  }, [mapStoresToProps, getStoreInspector]);
  var bouncedHandleStoreMutation = useCallback( // Synchronous mapping on SSR
  canUseDOM && !synchronousUpdate ? bounce(handleStoreMutation, 10) : handleStoreMutation, [handleStoreMutation, synchronousUpdate]);
  useIsomorphicLayoutEffect(function () {
    isMounted.current = true;
    referencedStores.current.forEach(function (store) {
      getStore(store).on(bouncedHandleStoreMutation);
    });
    return function () {
      referencedStores.current.forEach(function (store) {
        getStore(store).off(bouncedHandleStoreMutation);
      });
    };
  }, [bouncedHandleStoreMutation]);
  return latestState.current;
};

var connectToStores = function connectToStores(mapStoresToProps) {
  return function (Component) {
    return forwardRef(function (props, ref) {
      var mappedProps = useStore(function (getStore) {
        return mapStoresToProps(getStore, props);
      });
      return createElement(Component, Object.assign(Object.assign({
        ref: ref
      }, props), mappedProps));
    });
  };
};

export { FleurContext, connectToStores, createElementWithContext, useFleurContext, useStore, withFleurContext };
